import { useDragOver } from '@minoru/react-dnd-treeview';
import { Typography, alpha, styled } from '@mui/material';
import cn from 'classnames';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import {
  bindHover,
  bindPopover,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useRef } from 'react';

import { DocModel } from 'src/@types/docs';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { NavItem } from 'src/features/Navbar/NavItem';
import { DocAvatar } from 'src/features/doc/DocAvatar';
import useActiveLink from 'src/hooks/useActiveLink';
import { TreeItem } from './types';

type Props = {
  node: TreeItem;
  depth: number;
  isOpen: boolean;
  onToggle: (_id: DocModel['id']) => void;
  // onClick: (_id: string) => void;
  // deleteDoc(_id?: string): Promise<Doc>;
  // activeNodeId?: string;
};

export const CustomNode = ({
  node,
  // deleteDoc,
  depth,
  onToggle,
  isOpen,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const docUrl = PATH_DASHBOARD.docs.view(node.id as string);
  const { id } = node;

  const { active } = useActiveLink(docUrl);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'hover-popover',
  });

  const handleToggle = () => {
    onToggle(node.id);
  };

  const dragOverProps = useDragOver(id, isOpen, onToggle);

  const contentOverflows =
    contentRef?.current &&
    contentRef.current.scrollWidth > contentRef.current.clientWidth;

  return (
    <StyledContainer
      ref={containerRef}
      className={cn({ contentOverflows })}
      {...bindHover(popupState)}
    >
      <NavItem
        {...dragOverProps}
        path={docUrl}
        depth={1}
        hasSubNav={node.data?.hasChildren}
        toggleSubNav={handleToggle}
        icon={<DocAvatar key={node.text} icon={node.data?.icon?.value} />}
        isTreeView={true}
        isSubNavOpen={isOpen}
        actionAlwaysVisible
        sx={{
          marginBottom: 0.5,
        }}
      >
        <div
          id="node-text"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          ref={contentRef}
        >
          {node.text}
        </div>
      </NavItem>
      {contentOverflows && (
        <StyledHoverPopover
          {...bindPopover(popupState)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          hideBackdrop
          disableScrollLock
          transitionDuration={0}
          disableAutoFocus
          disableEnforceFocus
          disableEscapeKeyDown
          disableRestoreFocus
          className={cn({ isRoot: node.parent === 'root', active })}
          open={popupState.isOpen}
          slotProps={{
            paper: {
              style: {
                pointerEvents: 'none',
              },
            },
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <DocAvatar key={node.text} icon={node.data?.icon?.value} />
          <Typography textTransform="capitalize">{node.text}</Typography>
        </StyledHoverPopover>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
  '&.contentOverflows': {
    '&:hover': {
      '& .MuiMenuItem-root ': {
        backgroundColor: 'transparent',
        '& #node-text': {
          visibility: 'hidden',
        },
      },
    },
  },
}));

const StyledHoverPopover = styled(HoverPopover)(({ theme }) => ({
  padding: 0,
  margin: 0,
  '& .MuiPopover-paper': {
    display: 'flex',
    alignItems: 'center',
    height: 'fit-content',
    pointerEvents: 'none',
    padding: theme.spacing(0.25, 2, 0.25, 3.75),
    margin: theme.spacing(-0.025, 0, 0, 0),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    gap: theme.spacing(0.5),
    border: 'none',
  },
  '&.isRoot': {
    '& .MuiPopover-paper': {
      marginLeft: theme.spacing(-0.5),
    },
  },
  '&.active': {
    '& .MuiPopover-paper': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(
        theme.palette.primary.main,
        theme.palette.action.selectedOpacity,
      ),
    },
  },
}));
